import { FC } from "react";
import {
  renderNodeRule,
  renderMarkRule,
  StructuredText,
  StructuredTextDocument,
  StructuredTextGraphQlResponse,
} from "react-datocms/structured-text";
import { isList, isListItem, isParagraph, isLink, isHeading } from "datocms-structured-text-utils";
import { render as toPlainText } from "datocms-structured-text-to-plain-text";

import Typography from "@/components/Typography/Typography";

type Wrapper = (children: React.ReactNode, key: string) => JSX.Element;

interface CustomStructuredTextProps {
  data: StructuredTextGraphQlResponse | StructuredTextDocument;
  paragraphWrapper?: Wrapper;
  listWrapper?: Wrapper;
  listItemWrapper?: Wrapper;
  boldWrapper?: Wrapper;
  h1Wrapper?: Wrapper;
  h2Wrapper?: Wrapper;
  h3Wrapper?: Wrapper;
  h4Wrapper?: Wrapper;
  emptyLineHeight?: string;
  linkClassName?: string;
}

const DefaultTypographyWrapper: Wrapper = (children, key) => (
  <Typography key={key} variant="paragraph-light" className="!text-base">
    {children}
  </Typography>
);

const DefaultBoldWrapper: Wrapper = (children, key) => (
  <Typography key={key} variant="span-bold">
    {children}
  </Typography>
);

const DefaultListWrapper: Wrapper = (children, key) => (
  <div key={key} className="pl-6">
    {children}
  </div>
);

const DefaultHeading1Wrapper: Wrapper = (children, key) => (
  <Typography key={key} variant="hero-title">
    {children}
  </Typography>
);

const DefaultHeading2Wrapper: Wrapper = (children, key) => (
  <Typography key={key} variant="section-title">
    {children}
  </Typography>
);

const DefaultHeading3Wrapper: Wrapper = (children, key) => (
  <Typography key={key} variant="box-title">
    {children}
  </Typography>
);

const DefaultHeading4Wrapper: Wrapper = (children, key) => (
  <Typography key={key} variant="hero-subtitle">
    {children}
  </Typography>
);

const CustomStructuredText: FC<CustomStructuredTextProps> = ({
  data,
  paragraphWrapper = DefaultTypographyWrapper,
  boldWrapper = DefaultBoldWrapper,
  listWrapper = DefaultListWrapper,
  listItemWrapper = DefaultTypographyWrapper,
  h1Wrapper = DefaultHeading1Wrapper,
  h2Wrapper = DefaultHeading2Wrapper,
  h3Wrapper = DefaultHeading3Wrapper,
  h4Wrapper = DefaultHeading4Wrapper,
  emptyLineHeight = "1.25rem",
  linkClassName,
}) => {
  let parsedBlock: StructuredTextGraphQlResponse | StructuredTextDocument = data;
  if ((data as StructuredTextDocument)?.schema) {
    const localData: StructuredTextDocument = data as StructuredTextDocument;

    parsedBlock = {
      schema: localData.schema,
      document: {
        type: localData.document.type,
        children: localData.document.children.map((item) => {
          if (item.type === "paragraph") {
            // if there are children that have empty span then convert them to line break
            const paraChildren = item.children.map((childItem) => {
              return childItem.type === "span" && !childItem.value
                ? {
                    type: "span",
                    value: "\r\n",
                  }
                : childItem;
            });

            return {
              ...item,
              children: paraChildren,
            };
          }

          return item;
        }),
      },
    } as StructuredTextDocument;
  }

  return (
    <div className="whitespace-break-spaces">
      <StructuredText
        data={parsedBlock}
        customNodeRules={[
          renderNodeRule(isList, ({ adapter: { renderNode }, children, key, node }) => {
            let listTag = node.style === "numbered" ? "ol" : "ul";
            let classes = "";
            if (node.style === "numbered") {
              listTag = "ol";
              classes = "list-decimal marker:text-colorPrimary-300 marker:text-sm";
            } else {
              listTag = "ul";
              classes = "list-disc marker:text-colorPrimary-300";
            }

            const list = renderNode(listTag, { key, className: classes }, children);
            return listWrapper ? listWrapper(list, `list_wrapper_${key}`) : list;
          }),
          renderNodeRule(isListItem, ({ key, node }) => {
            return (
              <li key={key}>{listItemWrapper(toPlainText(node), `list_item_wrapper_${key}`)}</li>
            );
          }),
          renderNodeRule(isParagraph, ({ adapter: { renderNode }, children, key, node }) => {
            if (!toPlainText(node))
              return renderNode("div", { key, style: { marginTop: emptyLineHeight } });

            const paragraph = renderNode("span", { key }, children);
            return paragraphWrapper(paragraph, `para_wrapper_${key}`);
          }),
          renderNodeRule(isLink, ({ children, key, node }) => {
            return (
              <a
                key={key}
                target={node.meta && node.meta[0]?.value}
                href={node.url}
                className={linkClassName}
                rel="noopener noreferrer"
              >
                {children}
              </a>
            );
          }),
          renderNodeRule(isHeading, ({ adapter: { renderNode }, node, children, key }) => {
            const headingTag = `h${node.level}`;
            let headingWrapper = h1Wrapper;
            switch (headingTag) {
              case "h1":
                headingWrapper = h1Wrapper;
                break;
              case "h2":
                headingWrapper = h2Wrapper;
                break;
              case "h3":
                headingWrapper = h3Wrapper;
                break;
              case "h4":
                headingWrapper = h4Wrapper;
                break;
            }
            const heading = renderNode("span", { key }, children);
            return headingWrapper(heading, `heading_wrapper_${key}`);
          }),
        ]}
        customMarkRules={[
          renderMarkRule("strong", ({ adapter: { renderNode }, children, key }) => {
            const strong = renderNode("span", { key }, children);
            return boldWrapper(strong, `strong_wrapper_${key}`);
          }),
        ]}
      />
    </div>
  );
};

export default CustomStructuredText;
